import { useState, useEffect, useCallback, useMemo } from 'react';
import { ApiService } from '../Services/APIService.jsx';

/**
 * Custom hook for managing comments on AI summaries
 */
export const useComments = (summaryId) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Calculate stats from comments array instead of fetching from backend
  const stats = useMemo(() => {
    const topLevelComments = comments.length;
    let totalReplies = 0;
    
    // Count all replies recursively
    const countReplies = (comment) => {
      if (comment.replies && comment.replies.length > 0) {
        totalReplies += comment.replies.length;
        comment.replies.forEach(countReplies);
      }
    };
    
    comments.forEach(countReplies);
    
    return {
      totalComments: topLevelComments + totalReplies,
      topLevelComments,
      replies: totalReplies
    };
  }, [comments]);

  /**
   * Fetch all comments for a summary
   */
  const fetchComments = useCallback(async () => {
    if (!summaryId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.get(`/comments/summary/${summaryId}`);
      setComments(response.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [summaryId]);


  /**
   * Create a new comment
   */
  const createComment = useCallback(async (commentData) => {
    console.log('🔵 useComments.createComment called');
    console.log('🔵 Comment data:', commentData);
    
    try {
      console.log('🔵 Making API call to /comments');
      const response = await ApiService.post('/comments', commentData);
      console.log('🔵 API response:', response);
      
      // Add the new comment to the local state
      setComments(prevComments => {
        // If it's a reply, find the parent comment and add it to its replies
        if (commentData.parentCommentId) {
          return prevComments.map(comment => {
            if (comment.id === commentData.parentCommentId) {
              return {
                ...comment,
                replies: [...(comment.replies || []), response.data]
              };
            }
            return comment;
          });
        } else {
          // If it's a top-level comment, add it to the main list
          return [...prevComments, response.data];
        }
      });

      return response.data;
    } catch (err) {
      console.error('Error creating comment:', err);
      setError('Failed to create comment');
      throw err;
    }
  }, []);

  /**
   * Update a comment
   */
  const updateComment = useCallback(async (commentId, content) => {
    try {
      const response = await ApiService.put(`/comments/${commentId}`, { content });
      
      // Update the comment in local state
      setComments(prevComments => {
        return prevComments.map(comment => {
          if (comment.id === commentId) {
            return response.data;
          }
          // Check replies
          if (comment.replies) {
            const updatedReplies = comment.replies.map(reply => 
              reply.id === commentId ? response.data : reply
            );
            return { ...comment, replies: updatedReplies };
          }
          return comment;
        });
      });

      return response.data;
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Failed to update comment');
      throw err;
    }
  }, []);

  /**
   * Delete a comment
   */
  const deleteComment = useCallback(async (commentId) => {
    try {
      await ApiService.delete(`/comments/${commentId}`);
      
      // Remove the comment from local state
      setComments(prevComments => {
        return prevComments.map(comment => {
          if (comment.id === commentId) {
            return null; // Mark for removal
          }
          // Check replies
          if (comment.replies) {
            const filteredReplies = comment.replies.filter(reply => reply.id !== commentId);
            return { ...comment, replies: filteredReplies };
          }
          return comment;
        }).filter(Boolean); // Remove null entries
      });

      return true;
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment');
      throw err;
    }
  }, []);

  /**
   * Reply to a comment
   */
  const replyToComment = useCallback(async (parentCommentId, content) => {
    try {
      const response = await ApiService.post(`/comments/${parentCommentId}/reply`, { content });
      
      // Add the reply to the parent comment
      setComments(prevComments => {
        return prevComments.map(comment => {
          if (comment.id === parentCommentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), response.data]
            };
          }
          return comment;
        });
      });

      return response.data;
    } catch (err) {
      console.error('Error creating reply:', err);
      setError('Failed to create reply');
      throw err;
    }
  }, []);

  // Fetch comments when summaryId changes
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return {
    comments,
    loading,
    error,
    stats,
    createComment,
    updateComment,
    deleteComment,
    replyToComment,
    refetch: fetchComments
  };
};
